<script lang="ts" setup>
import { useRoute } from '#vue-router'

import { ROUTE_PATHS } from '@/utils/route'
import Loader from '@/components/Loader/Loader.vue'
import { useArticlePage } from '@/domains/articles/composables/useArticlePage'
import useArticlePageHead from '@/domains/articles/composables/useArticlePageHead'
import { useRouteLink } from '@/composables/useRouteLink'

const { routeLink } = useRouteLink()
const location = useRoute()
const { isMobile } = useBSBreakpoints()
const showArticle = ref(false)

const { article, articles } = await useArticlePage()

if (article.value) {
  showArticle.value = true
  const { title, link, meta, I18nParams } = useArticlePageHead(article.value)
  const setI18nParams = useSetI18nParams()
  setI18nParams(I18nParams)
  useHead({
    title,
    meta,
    link,
  })
}
else {
  throw createError({
    statusCode: 400,
    fatal: true,
  })
}

watch(location, () => {
  showArticle.value = false
})

</script>

<template>
  <div>
    <div
      v-if="!showArticle || !article"
      class="article__loader"
    >
      <Loader />
    </div>
    <article
      v-if="showArticle && article"
      class="article"
    >
      <header class="article__header">
        <h1 class="article__header_title">
          {{ article?.title }}
        </h1>
        <p class="article__header_subtitle">
          {{ article?.description }}
        </p>
        <div class="article__header_meta">
          <time
            v-if="article?.date"
            :datetime="article?.date"
            class="article__header_date"
          >
            {{ $t('articles.publication') }} {{ article?.date }}
          </time>
          <span
            v-if="article?.author.name"
            class="article__header_author"
          >
            {{ article?.author.name }}
          </span>
        </div>
        <div class="article__header_cover">
          <img
            v-if="isMobile"
            :src="article?.image"
            :alt="article?.title"
            class="article__header_image"
          >
          <img
            v-else
            :src="article?.thumbnailWeb"
            :alt="article?.title"
            class="article__header_image"
          >
        </div>
      </header>
      <div
        class="article__body"
      >
        <div
          class="article__content"
          v-html="article?.content"
        />
      </div>
      <div
        v-if="article?.author.image"
        class="article__author"
      >
        <img
          :src="article.author.image.secure_url"
          :alt="article?.author.name"
          class="article__author_image"
        >
        <div class="article__author_content">
          <h4 class="article__author_name">
            {{ article?.author.name }}
          </h4>
          <p class="article__author_title">
            {{ article?.author.title }}
          </p>
          <p class="article__author_description">
            {{ article?.author.description }}
          </p>
        </div>
      </div>
    </article>
    <div class="article__articles">
      <CardCarousel
        v-if="articles"
        :header="$t('articles.header')"
        color="gray"
        :slides="articles"
        :button="{
          text: $t('articles.buttonText'),
          to: routeLink(ROUTE_PATHS.ARTICLES),
        }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.article {
  margin: 0 auto;
  padding: 8.8rem 2.4rem 4.8rem 2.4rem;
  background-color: $gray-100;

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: calc(100vh - 8.8rem);
  }

  &__header {

    @include atM {
      max-width: 120rem;
      margin: 6.4rem auto 0 auto;
    }

    &_title {
      font-weight: 500;
      font-size: 3.2rem;
      line-height: 4rem;
      text-align: center;

      @include atM {
        max-width: 111.6rem;
        margin: 0 auto;
        font-size: 6rem;
        line-height: 8.8rem;
      }
    }

    &_subtitle {
      margin-top: 1.6rem;
      font-weight: 500;
      font-size: 2rem;
      line-height: 2.8rem;
      text-align: center;

      @include atM {
        max-width: 83rem;
        margin: 3.2rem auto 0 auto;
        font-size: 2.4rem;
        line-height: 3.2rem;
      }
    }

    &_meta {
      display: block;
      margin-top: 3.2rem;
      text-align: center;

      @include atM {
        margin-top: 4.8rem;
      }
    }

    &_date,
    &_author {
      display: inline-block;
      color: $text-200;
      font-weight: 500;
      font-size: 1.6rem;
      line-height: 2.4rem;
    }

    &_date {
      padding-right: 1.6rem;
      color: $text-200;
    }

    &_author {
      padding-left: 1.6rem;
      color: $text-300;
      border-left: 0.1rem solid $gray-300;
    }

    &_cover {
      width: 100%;
      height: 18.5rem;
      margin-top: 2.8rem;
      margin-bottom: 1.6rem;
      overflow: hidden;
      border-radius: 1.2rem;

      @include atM {
        height: 75.4rem;
        margin-top: 5.6rem;
        margin-bottom: 5rem;
      }
    }

    &_image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  &__body {
    padding-bottom: 4rem;
    border-bottom: 0.1rem solid $gray-300;

    @include atM {
      padding-bottom: 5.6rem;
    }

    &_icons {
      display: flex;
      gap: 0.8rem;
      margin-bottom: 1.6rem;
    }
  }

  &__content {

    :deep() {
      hr {
        height: 0.1rem;
        margin: 0  0 2.4rem 0;
        background: $gray-300;
      }

      .richtext__imgContainer, .richtext__imgContainer a {
        display: flex;
        align-items: center;
        width: 100%;
      }

      .richtext__img {
        max-width: 900px;
        width: 100%;
        margin: 40px auto;
        aspect-ratio: 16 / 9;
      }

      h1,
      h2,
      h3,
      h4,
      h5 {
        margin-bottom: 2.4rem;
        font-weight: 500;
        line-height: 1.2;
      }

      h1 {
        font-size: 2.8rem;
      }

      h2 {
        font-size: 2.6rem;
      }

      h3 {
        font-size: 2.4rem;
      }

      h4 {
        font-size: 2.2rem;
      }

      h5 {
        font-size: 2rem;
      }

      p {
        margin-bottom: 2.4rem;
        font-weight: 400;
        font-size: 1.8rem;
        line-height: 2.4rem;
      }

      b, strong {
        font-weight: 600;
      }

      i {
        font-style: italic;
      }

      u {
        text-decoration: underline;
      }

      a {
        color: $link;
        font-weight: 400;
      }

      ul, ol {
        padding-left: 2rem;
        li {
          &::marker {
            font-size: 1.8rem;
          }

          p {
            margin-bottom: 0.8rem
          }
        }
      }

      ul {
        list-style-type: disc;
      }

      ol {
        list-style-type: decimal;
      }

      blockquote {
        padding-left: 1.6rem;
        border-left: 0.8rem solid $gray-300;
      }

      table {
        width: 100%;
        margin-bottom: 2.4rem;
        font-weight: 400;
        border: 1px solid $gray-300;

        tr {
          &:nth-of-type(odd) {
            background-color: $white;
          }

          &:nth-of-type(even) {
            background-color: $gray-100;
          }

          th, td {
            padding: 1.6rem;
            color: $text-400;
            font-weight: 400;
            font-size: 1.4rem;
            border: 1px solid $gray-300;

            p {
              margin-bottom: 0;
            }

            &:first-of-type {
              font-weight: 600;
            }
          }

          th {
            font-weight: 600;
          }
        }
      }
    }

    @include atM {
      max-width: 100rem;
      margin: 0 auto;

      :deep() {
        h1,
        h2,
        h3,
        h4,
        h5 {
          margin-bottom: 3rem;
          line-height: 1.2;
        }

        h1 {
          font-size: 4rem;
        }

        h2 {
          font-size: 3.6rem;
        }

        h3 {
          font-size: 3.2rem;
        }

        h4 {
          font-size: 2.8rem;
        }

        h5 {
          font-size: 2.4rem;
        }

        p {
          margin-bottom: 4.8rem;
          font-size: 2rem;
          line-height: 3rem;
        }

        ul, ol {
          li {
            &::marker {
              font-size: 2rem;
            }
          }
        }

        blockquote {
          padding-left: 2.4rem;
        }

        table {
          margin-bottom: 4.8rem;
          th, td {
            font-size: 1.6rem;
          }
        }
      }
    }
  }

  &__author {
    display: flex;
    gap: 2.4rem;
    padding: 4rem 0;
    border-bottom: 0.1rem solid $gray-300;

    @include atM {
      gap: 4rem;
      max-width: 100rem;
      margin: 0 auto;
      padding: 5.6rem 0;
    }

    &_image {
      min-width: 7.2rem;
      width: 7.2rem;
      height: 7.2rem;
      overflow: hidden;
      border-radius: 50%;
      object-fit: cover;

      @include atM {
        min-width: 15rem;
        width: 15rem;
        height: 15rem;
      }
    }

    &_name {
      margin-bottom: 1.6rem;
      font-weight: 500;
      font-size: 2.4rem;
      line-height: 3.2rem;
    }

    &_title {
      margin-bottom: 1.6rem;
      color: $text-200;
      font-weight: 500;
      font-size: 2rem;
      line-height: 2.4rem;
    }

    &_description {
      font-weight: 400;
      font-size: 2rem;
      line-height: 3rem;
    }
  }
}
</style>
